import type { IntlShape } from 'react-intl'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { type FlightSavedItemData } from 'tripPlanner/api/types'
import { dayOffset, getSavedFlightBoundaryDateParts } from './bookedFlightTileUtils'

type CalendarDayParts = { year: number; month: number; day: number }

function isSameCalendarDay(a: CalendarDayParts, b: CalendarDayParts): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day
}

function formatCalendarDay(parts: CalendarDayParts, intl: IntlShape): string {
  return intlDateFormatter(
    new Date(parts.year, parts.month - 1, parts.day),
    INTL_SHORT_DAY_NAME_DAY_MONTH,
    intl.locale,
  )
}

export function formatSavedFlightDateLabel(
  data: FlightSavedItemData | undefined,
  boundary: 'departure' | 'arrival',
  intl: IntlShape,
): string | undefined {
  const parts = getSavedFlightBoundaryDateParts(data, boundary)
  if (!parts) return undefined
  return formatCalendarDay(parts, intl)
}

export function getFlightDateRangeLabel(
  data: FlightSavedItemData | undefined,
  intl: IntlShape,
): string | undefined {
  const departureParts = getSavedFlightBoundaryDateParts(data, 'departure')
  const arrivalParts = getSavedFlightBoundaryDateParts(data, 'arrival')
  const departureDate = departureParts ? formatCalendarDay(departureParts, intl) : undefined
  const arrivalDate = arrivalParts ? formatCalendarDay(arrivalParts, intl) : undefined

  if (!departureDate && !arrivalDate) return undefined
  if (!departureDate || !arrivalDate || !departureParts || !arrivalParts) {
    return departureDate ?? arrivalDate
  }
  if (isSameCalendarDay(departureParts, arrivalParts)) return departureDate

  const calendarDayOffset = dayOffset(
    data?.departureDate ?? data?.departureTime,
    data?.arrivalDate ?? data?.arrivalTime,
  )

  if (calendarDayOffset === 0) {
    return intl.formatMessage({
      id: 'SGeCkuL_2K_c9sEbWC_7',
      defaultMessage: '{departureDate} → {arrivalDate}',
      description: 'Flight departure and arrival dates on the same displayed span with no calendar-day offset in trip planner',
    }, { departureDate, arrivalDate })
  }

  const offsetLabel = intl.formatMessage({
    id: 'BFhM9WE9PpGWq7UlwKqY',
    defaultMessage: '({sign}{count, plural, one {# day} other {# days}})',
    description: 'Calendar-day difference between flight departure and arrival in a trip planner summary',
  }, {
    sign: calendarDayOffset > 0 ? '+' : '−',
    count: Math.abs(calendarDayOffset),
  })

  return intl.formatMessage({
    id: 'ypFDLn9J9nTI8kXeWnLd',
    defaultMessage: '{departureDate} → {arrivalDate} {offset}',
    description: 'Flight departure and arrival dates with a calendar-day offset label in the trip planner transport summary',
  }, {
    departureDate,
    arrivalDate,
    offset: offsetLabel,
  })
}

export function getSavedFlightSummaryDateLabel(
  data: FlightSavedItemData | undefined,
  isReturn: boolean,
  reversed: boolean,
  intl: IntlShape,
): string | undefined {
  if (!data) return undefined

  if (data.flightGroupType === 'multi_city') {
    const departureParts = getSavedFlightBoundaryDateParts(data, 'departure')
    const arrivalParts = getSavedFlightBoundaryDateParts(data, 'arrival')
    const departureDate = departureParts ? formatCalendarDay(departureParts, intl) : undefined
    const arrivalDate = arrivalParts ? formatCalendarDay(arrivalParts, intl) : undefined

    if (!departureDate && !arrivalDate) return undefined
    if (!departureDate || !arrivalDate || !departureParts || !arrivalParts) {
      return departureDate ?? arrivalDate
    }
    if (isSameCalendarDay(departureParts, arrivalParts)) return departureDate

    return intl.formatMessage({
      id: '1LwXJffQdJSze1q9rl_2',
      defaultMessage: '{departureDate} - {arrivalDate}',
      description: 'Multi-city flight departure and arrival date range shown on the trip planner transport summary',
    }, { departureDate, arrivalDate })
  }

  if (!isReturn) {
    return getFlightDateRangeLabel(data, intl)
  }

  return formatSavedFlightDateLabel(data, isReturn && reversed ? 'departure' : 'arrival', intl)
}

export function formatFlightAirportRoute(
  departureAirport: string,
  arrivalAirport: string,
  intl: IntlShape,
): string {
  return intl.formatMessage({
    id: 'X3MDYvgnRvuk2z8ltMeR',
    defaultMessage: '{departureAirport} → {arrivalAirport}',
    description: 'Airport code route label for a selected flight on the trip planner transport tile',
  }, { departureAirport, arrivalAirport })
}

/** Airport code route of a saved flight leg, falling back to whichever airport is known. */
export function formatSavedFlightRoute(data: FlightSavedItemData, intl: IntlShape): string {
  const { departureAirport, arrivalAirport } = data
  if (departureAirport && arrivalAirport) return formatFlightAirportRoute(departureAirport, arrivalAirport, intl)
  return departureAirport ?? arrivalAirport ?? ''
}
